import { newId } from '@trips/shared/ids';
import type { ClientWritableTable, Mutation, SyncRecordMap } from '@trips/shared/schema';
import { db, type OutboxEntry } from './db';
import { getState, setSync, upsertRecords } from './store';

/**
 * Every edit lands in memory first (so the screen updates instantly), then in IndexedDB
 * with an outbox entry, then reaches the server when the sync engine pushes.
 */

let seq = Date.now();
let lastStamp = 0;
let pushHook: () => void = () => {};

/** Pending edits by `${table}/${id}`, used to keep local changes on top of pulled rows. */
export const pendingByRow = new Map<string, OutboxEntry[]>();

export function onLocalWrite(fn: () => void) {
  pushHook = fn;
}

/** Strictly increasing edit time on this device. */
export function stamp(): number {
  const now = Date.now();
  lastStamp = now > lastStamp ? now : lastStamp + 1;
  return lastStamp;
}

export async function loadPending() {
  const all = await db.outbox.orderBy('seq').toArray();
  pendingByRow.clear();
  for (const e of all) indexPending(e);
  if (all.length) seq = Math.max(seq, all[all.length - 1]!.seq + 1);
  setSync({ pending: all.length });
}

function indexPending(e: OutboxEntry) {
  const key = `${e.table}/${e.rowId}`;
  const list = pendingByRow.get(key) ?? [];
  list.push(e);
  pendingByRow.set(key, list);
}

export function unindexPending(ids: Set<string>) {
  for (const [key, list] of pendingByRow) {
    const kept = list.filter((e) => !ids.has(e.id));
    if (kept.length) pendingByRow.set(key, kept);
    else pendingByRow.delete(key);
  }
}

/** Applies pending local edits on top of a record from the server. */
export function rebase<T extends ClientWritableTable>(
  table: T,
  record: SyncRecordMap[T],
): SyncRecordMap[T] {
  const pending = pendingByRow.get(`${table}/${record.id}`);
  if (!pending) return record;
  let out = record as Record<string, unknown>;
  for (const e of pending) {
    if (e.updatedAt < record.updatedAt && e.op === 'put') continue;
    if (e.op === 'put') out = { ...out, ...(e.data ?? {}) };
    else if (e.op === 'patch')
      out = { ...out, ...(e.data ?? {}), updatedAt: Math.max(Number(out.updatedAt), e.updatedAt) };
    else out = { ...out, deleted: true };
  }
  return out as SyncRecordMap[T];
}

async function enqueue(m: Mutation, record: Record<string, unknown>, table: ClientWritableTable) {
  const entry: OutboxEntry = { ...m, seq: seq++, attempts: 0 };
  indexPending(entry);
  await db.transaction('rw', db.outbox, db.records(table), async () => {
    await db.records(table).put(record as never);
    await db.outbox.put(entry);
  });
  setSync({ pending: getState().sync.pending + 1 });
  pushHook();
}

type Writable<T extends ClientWritableTable> = Omit<
  SyncRecordMap[T],
  'rev' | 'updatedAt' | 'createdAt' | 'deleted'
> &
  Partial<Pick<SyncRecordMap[T], 'createdAt'>>;

/** Creates (or fully replaces) a record. */
export async function put<T extends ClientWritableTable>(
  table: T,
  value: Writable<T>,
): Promise<SyncRecordMap[T]> {
  const updatedAt = stamp();
  const record = {
    ...value,
    rev: getState().tables[table].get(value.id)?.rev ?? 0,
    updatedAt,
    createdAt: value.createdAt ?? updatedAt,
    deleted: false,
  } as unknown as SyncRecordMap[T];
  upsertRecords(table, [record]);
  await enqueue(
    {
      id: newId('mut'),
      table,
      rowId: record.id,
      op: 'put',
      data: record as unknown as Record<string, unknown>,
      updatedAt,
    },
    record as unknown as Record<string, unknown>,
    table,
  );
  return record;
}

/** Changes some fields of a record. */
export async function patch<T extends ClientWritableTable>(
  table: T,
  id: string,
  fields: Partial<Omit<SyncRecordMap[T], 'id' | 'rev' | 'updatedAt' | 'createdAt' | 'deleted'>>,
): Promise<void> {
  const current = getState().tables[table].get(id);
  if (!current) throw new Error(`No ${table} record ${id}`);
  const updatedAt = stamp();
  const record = { ...current, ...fields, updatedAt } as SyncRecordMap[T];
  upsertRecords(table, [record]);
  await enqueue(
    {
      id: newId('mut'),
      table,
      rowId: id,
      op: 'patch',
      data: fields as Record<string, unknown>,
      updatedAt,
    },
    record as unknown as Record<string, unknown>,
    table,
  );
}

/** Soft-deletes a record. */
export async function remove<T extends ClientWritableTable>(table: T, id: string): Promise<void> {
  const current = getState().tables[table].get(id);
  if (!current) return;
  const updatedAt = stamp();
  const record = { ...current, deleted: true, updatedAt } as SyncRecordMap[T];
  upsertRecords(table, [record]);
  await enqueue(
    { id: newId('mut'), table, rowId: id, op: 'delete', updatedAt },
    record as unknown as Record<string, unknown>,
    table,
  );
}
